import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, createUser, updateUser } from '../services/api';
import { showSuccessNotification, showErrorNotification } from '@core/utils/apiErrorHandler';
import type { CreateUserRequest, UpdateUserRequest, USER_STATUS } from '../types';

export const useUsers = () => {
  const queryClient = useQueryClient();

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: () => {
      showSuccessNotification('User created successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to create user');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: UpdateUserRequest }) => updateUser(userId, data),
    onSuccess: () => {
      showSuccessNotification('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to update user');
    },
  });

  const blockMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: UpdateUserRequest }) => updateUser(userId, data),
    onSuccess: (_, variables) => {
      const isBlocking = variables.data.status === 'Blocked';
      showSuccessNotification(isBlocking ? 'User blocked successfully' : 'User unblocked successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to update user status');
    },
  });

  return {
    users,
    isLoading,
    isError,
    error,
    refetch,
    createUser: createMutation.mutateAsync,
    updateUser: updateMutation.mutateAsync,
    blockUser: blockMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isBlocking: blockMutation.isPending,
  };
};
